import { Event, Session } from '@opentok/client';
import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { getUserIdFromConnection } from '@/helpers/user';
import { useSessionContext } from './useSession';

// eslint-disable-next-line no-shadow
export enum SignalTypes {
	SUGGEST_DEVICES = 'suggest_devices',
	LAST_INTERACTIVE = 'last_interactive',
	REFRESH_BOARD = 'refresh_board',
	VIDEO_PIN_STATUS = 'video_pin_status',
	BOARD_PINS_CHANGED = 'board_pins_changed',
	BOARD_PINS_REQUEST = 'board_pins_request',
}

type SignalPayload = Event<'signal', Session> & {
	type?: string;
	data?: string;
	from: OT.Connection | null;
};

type ProviderProps = {
	children?: ReactNode;
};

class SignalEmitter {
	callbacks: {
		[key in SignalTypes]: {
			[key: string]: (data: string, fromUserId: string) => void;
		};
	};

	constructor() {
		this.callbacks = {
			[SignalTypes.SUGGEST_DEVICES]: {},
			[SignalTypes.LAST_INTERACTIVE]: {},
			[SignalTypes.REFRESH_BOARD]: {},
			[SignalTypes.VIDEO_PIN_STATUS]: {},
			[SignalTypes.BOARD_PINS_CHANGED]: {},
			[SignalTypes.BOARD_PINS_REQUEST]: {},
		};
	}

	on(signalType: SignalTypes, userId: string | '*', cb: (data: any, fromUserId: string) => void) {
		this.callbacks[signalType][userId] = cb;
	}

	off(signalType: SignalTypes, userId: string | '*') {
		delete this.callbacks[signalType][userId];
	}

	emit(signalType: SignalTypes, from: OT.Connection, data: any) {
		const userId = getUserIdFromConnection(from);

		const callback =
			this.callbacks[signalType][userId] || this.callbacks[signalType]['*'];

		callback?.(data, userId);
	}
}

type ContextType = {
	emitter: SignalEmitter;
};

const Context = createContext({} as ContextType);

const SignalProvider: FC<ProviderProps> = ({ children }) => {
	const emitterRef = useRef<SignalEmitter>(new SignalEmitter());
	const { getSession } = useSessionContext();
	const session = getSession();

	useEffect(() => {
		if (!session) return;
		const handleSignal = (event: SignalPayload) => {
			const { type, data, from } = event;
			const [, signalName] = type.split(':');

			if (signalName && data && from) {
				emitterRef.current.emit(signalName as SignalTypes, from, data);
			}
		};

		session.on('signal', handleSignal);

		return () => {
			session.off('signal', handleSignal);
		};
	}, [session]);

	const value = useMemo(() => ({ emitter: emitterRef.current }), []);

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

export function useSendSignal(
	signalType: SignalTypes
): (data: any, to?: OT.Connection, successCallback?: () => void) => void {
	const { getSession } = useSessionContext();
	const session = getSession();

	const sendSignal = useCallback(
		(data: string, to?: OT.Connection, successCallback?: () => void) => {
			session?.signal(
				{
					type: signalType,
					data: JSON.stringify(data),
					...(to && { to }),
				},
				(error?: OT.OTError) => {
					if (error) {
						console.log(error);
					} else {
						successCallback?.();
					}
				}
			);
		},
		[signalType, session]
	);

	return sendSignal;
}

export function useSubscribeToSignal<T>(
	signalType: SignalTypes
): (fromUserId: string | '*', callback: (data: T, fromUserId: string) => void) => () => void {
	const { emitter } = useContext(Context);

	const subscribeToSignal = useCallback(
		(fromUserId: string | '*', callback: (data: T, fromUserId: string) => void) => {
			const deserialize = (data: string, senderId: string) =>
				callback(JSON.parse(data), senderId);
			emitter?.on(signalType, fromUserId, deserialize);

			return () => emitter?.off(signalType, fromUserId);
		},
		[emitter, signalType]
	);

	return subscribeToSignal;
}


export default SignalProvider;
